import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Briefcase, Sparkles, Users, LineChart, LogOut, MessageCircle, Rocket, Menu, X, MessageSquarePlus, Trash2, Loader2, LifeBuoy, ShieldCheck, Wand2, MessageSquareQuote } from "lucide-react";
import { Logo } from "@/components/Common";
import FeedbackDialog from "@/components/FeedbackDialog";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { resetAnalytics } from "@/lib/analytics";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STUDENT_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/business", label: "My business", icon: Briefcase, testid: "nav-business" },
  { to: "/ai-studio", label: "AI Studio", icon: Sparkles, testid: "nav-ai-studio" },
  { to: "/launch", label: "Launch Center", icon: Rocket, testid: "nav-launch" },
  { to: "/build", label: "Build Studio", icon: Wand2, testid: "nav-build" },
  { to: "/community", label: "Community", icon: MessageCircle, testid: "nav-community", disabled: true },
];

const FOUNDER_NAV = [
  { to: "/founder", label: "Dashboard", icon: LayoutDashboard, testid: "nav-founder-dashboard", end: true },
  { to: "/founder/students", label: "Students", icon: Users, testid: "nav-founder-students" },
  { to: "/founder/analytics", label: "Analytics", icon: LineChart, testid: "nav-founder-analytics" },
  { to: "/founder/admin", label: "Admin", icon: ShieldCheck, testid: "nav-founder-admin" },
  { to: "/founder/build-orders", label: "Build orders", icon: Wand2, testid: "nav-founder-build-orders" },
  { to: "/founder/testimonials", label: "Testimonials", icon: MessageSquareQuote, testid: "nav-founder-testimonials" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [config, setConfig] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const nav = user?.role === "founder" ? FOUNDER_NAV : STUDENT_NAV;

  useEffect(() => {
    api.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  const onDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete("/auth/account");
      resetAnalytics();
      await logout();
      toast.success("Your account and all its data have been deleted.");
      navigate("/", { replace: true });
    } catch {
      setDeleting(false);
      toast.error("Could not delete your account. Please try again.");
    }
  };

  const SidebarInner = (
    <>
      <div className="px-2">
        <Logo variant="white" height="h-7" />
      </div>
      <nav className="mt-8 flex flex-1 flex-col gap-1">
        {nav.map((item) => {
          const Icon = item.icon;
          if (item.disabled) {
            return (
              <span key={item.to} data-testid={item.testid} className="flex cursor-not-allowed items-center gap-3 rounded-lg px-3 py-2 text-sm text-brand-muted/50">
                <Icon size={17} strokeWidth={1.8} />
                {item.label}
                <span className="ml-auto text-[10px] uppercase tracking-wide">soon</span>
              </span>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all duration-200 ${
                  isActive ? "bg-brand text-white" : "text-brand-muted hover:bg-white/5 hover:text-brand-mutedHover"
                }`
              }
            >
              <Icon size={17} strokeWidth={1.8} />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="mt-4 border-t border-white/10 pt-4">
        <div className="flex items-center gap-3 px-2">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-brand text-xs font-semibold text-white">
            {(user?.name || "?").slice(0, 1).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-white">{user?.name}</p>
            <p className="truncate text-xs text-brand-muted">{user?.email}</p>
          </div>
        </div>
        <button data-testid="feedback-btn" onClick={() => { setFeedbackOpen(true); setOpen(false); }} className="mt-3 flex w-full items-center gap-2 rounded-lg bg-white/5 px-3 py-2 text-sm text-brand-mutedHover transition-all duration-200 hover:bg-white/10 hover:text-white">
          <MessageSquarePlus size={16} strokeWidth={1.8} /> Share feedback
        </button>
        <a href="/help" data-testid="help-link" className="mt-2 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-brand-muted transition-all duration-200 hover:bg-white/5 hover:text-white">
          <LifeBuoy size={16} strokeWidth={1.8} /> Help &amp; support
        </a>
        <button data-testid="logout-btn" onClick={onLogout} className="mt-2 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-brand-muted transition-all duration-200 hover:bg-white/5 hover:text-white">
          <LogOut size={16} strokeWidth={1.8} /> Sign out
        </button>
        <button data-testid="delete-account-btn" onClick={() => { setDeleteOpen(true); setOpen(false); }} className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs text-brand-muted/70 transition-all duration-200 hover:text-red-300">
          <Trash2 size={14} strokeWidth={1.8} /> Delete account
        </button>
      </div>
    </>
  );

  return (
    <div className="flex min-h-screen bg-[#FDFCFE]">
      {/* desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-20 hidden w-60 flex-col bg-brand-deep px-4 py-6 md:flex">
        {SidebarInner}
      </aside>

      {/* mobile top bar */}
      <div className="fixed inset-x-0 top-0 z-30 flex h-14 items-center justify-between bg-brand-deep px-4 md:hidden">
        <Logo variant="white" height="h-6" />
        <button data-testid="mobile-menu-btn" onClick={() => setOpen(true)} className="text-white" aria-label="Open menu">
          <Menu size={22} />
        </button>
      </div>

      {/* mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-40 md:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 flex w-64 flex-col bg-brand-deep px-4 py-6">
            <button onClick={() => setOpen(false)} className="absolute right-3 top-3 text-brand-muted" aria-label="Close menu"><X size={20} /></button>
            {SidebarInner}
          </aside>
        </div>
      )}

      <main className="flex-1 pt-14 md:ml-60 md:pt-0">
        {config?.early_access && (
          <div className="flex items-center justify-center gap-2 bg-brand-surface px-4 py-1.5 text-center text-xs font-medium text-brand-text" data-testid="beta-banner">
            <span>{config.beta_banner_text}</span>
            <span className="text-brand-muted">·</span>
            <button onClick={() => setFeedbackOpen(true)} className="underline underline-offset-2 hover:text-brand">Share feedback</button>
          </div>
        )}
        {children}
      </main>
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent data-testid="delete-account-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes your account, your business, every asset and draft you've created,
              and your progress. This cannot be undone. If you'd like a copy first, export your assets from
              My Business.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-account-cancel">Keep my account</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-account-confirm"
              onClick={(e) => { e.preventDefault(); onDeleteAccount(); }}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? <Loader2 size={15} className="mr-1 inline animate-spin" /> : null}
              Delete everything
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
